import { BluvoClient } from "./src/BluvoClient";
import { BluvoWebClient } from "./src/BluvoWebClient";

export * from "./generated";
export { BluvoClient } from "./src/BluvoClient";
export { BluvoWebClient, type QRCodeOptions } from "./src/BluvoWebClient";
// Workflow Types
export {
	WorkflowTypes,
	type QRCodeStatus,
	type QRCodeAuthWorkflowMessageBody,
	type WorkflowMessageBody,
} from "./src/WorkflowTypes";
// State Machine exports
export * from "./src/machines";
export type { SilentResumeWithdrawalFlowOptions } from "./src/machines/BluvoFlowClient";
export type { BluvoPreviewManagerOptions } from "./src/machines/BluvoPreviewManager";
export { BluvoPreviewManager } from "./src/machines/BluvoPreviewManager";
export * from "./src/types/flow.types";
export * from "./src/types/machine.types";
// Preview functionality (NEW - backwards compatible addition)
export * from "./src/types/preview.types";
export * from "./src/types/withdrawal.types";
export type { Subscription, TopicInfo } from "./src/WebSocketClient";
export { WebSocketClient } from "./src/WebSocketClient";
// Error handling utilities
export {
	ERROR_CODES,
	extractErrorCode,
	extractErrorTypeInfo,
	extractErrorResult,
	type ErrorCode,
	type ErrorTypeInfo,
	type BluvoError,
} from "./src/error-codes";

// Cache
export { BluvoCache, type BluvoCacheAdapter, type BluvoCacheOptions } from "./src/BluvoCache";

// API Types - Clean, self-explanatory type exports
// export * from './src/types/api.types';

/**
 * Create a fully configured Bluvo SDK client instance with a single function call.
 *
 * This is the recommended entry point for integrating the Bluvo SDK into your application.
 * It creates and initializes a client with your organization credentials, handling all the
 * necessary setup and authentication configuration automatically.
 *
 * The client provides access to all Bluvo API functionality through an intuitive, hierarchical
 * interface organized by domain (prices, wallets, transactions, etc.), making it easy to discover
 * and use the capabilities you need.
 *
 * @param credentials An object containing your Bluvo API credentials:
 *   @param orgId Your unique organization identifier provided during Bluvo onboarding.
 *                This identifies your organization across all projects and API calls.
 *   @param projectId The specific project identifier within your organization.
 *                    Different projects can be used for different applications or environments.
 *   @param apiKey Your secret API key that authorizes requests to the Bluvo API.
 *                 Handle this credential securely and never expose it in client-side code.
 *   @param customDomain Optional custom domain configuration to override default api-bluvo.com.
 *
 * @returns A fully initialized BluvoClient instance ready to make API calls.
 *
 * @example
 * // Create a new client instance
 * const bluvo = createClient({
 *   orgId: 'your-org-id',
 *   projectId: 'your-project-id',
 *   apiKey: 'your-api-key'
 * });
 *
 * // The client is ready to use
 * const btcPrice = await bluvo.prices.candlesticks('BTC', 'USDT');
 */
export function createClient({
	orgId,
	projectId,
	apiKey,
	customDomain,
}: {
	orgId: string;
	projectId: string;
	apiKey: string;
	customDomain?: string | "api-bluvo.com" | { api: string; ws: string };
}) {
	return BluvoClient.createClient({
		orgId,
		projectId,
		apiKey,
		customDomain,
	});
}

export function createSandboxClient({
	orgId,
	projectId,
	apiKey,
	customDomain,
}: {
	orgId: string;
	projectId: string;
	apiKey: string;
	customDomain?: string | "api-bluvo.com" | { api: string; ws: string };
}) {
	return BluvoClient.createClient({
		orgId,
		projectId,
		apiKey,
		sandbox: true,
		customDomain,
	});
}

export function createDevClient({
	orgId,
	projectId,
	apiKey,
	customDomain,
}: {
	orgId: string;
	projectId: string;
	apiKey: string;
	customDomain?: string | "api-bluvo.com" | { api: string; ws: string };
}) {
	return BluvoClient.createClient({
		orgId,
		projectId,
		apiKey,
		dev: true,
		customDomain,
	});
}

// createWebClient
/**
 * Create a BluvoWebClient instance for browser-based applications.
 *
 * This client is designed for use in web environments where you want to interact with
 * the Bluvo API without exposing sensitive API keys directly in client-side code.
 * It provides methods for OAuth2 authentication flows and other browser-specific features.
 *
 * @param orgId Your Bluvo organization identifier.
 * @param projectId Your Bluvo project identifier.
 * @param customDomain Optional custom domain configuration to override default api-bluvo.com.
 *
 * @returns A new BluvoWebClient instance configured for your organization and project.
 */
export function createWebClient({
	orgId,
	projectId,
	customDomain,
}: {
	orgId: string;
	projectId: string;
	customDomain?: string | "api-bluvo.com" | { api: string; ws: string };
}): BluvoWebClient {
	return BluvoWebClient.createClient({ orgId, projectId, customDomain });
}

/**
 * Create a BluvoWebClient instance for the sandbox environment.
 *
 * This client is configured to use the Bluvo sandbox/test environment,
 * which is useful for development and testing without affecting production data.
 *
 * @param orgId Your Bluvo organization identifier.
 * @param projectId Your Bluvo project identifier.
 * @param customDomain Optional custom domain configuration to override default api-bluvo.com.
 *
 * @returns A new BluvoWebClient instance configured for the sandbox environment.
 */
export function createSandboxWebClient({
	orgId,
	projectId,
	customDomain,
}: {
	orgId: string;
	projectId: string;
	customDomain?: string | "api-bluvo.com" | { api: string; ws: string };
}): BluvoWebClient {
	return BluvoWebClient.createClient({
		orgId,
		projectId,
		sandbox: true,
		customDomain,
	});
}

/**
 * Create a BluvoWebClient instance for local development.
 *
 * This client is configured to use localhost:8787 as the API endpoint,
 * which is useful for local development and testing with a local Bluvo backend.
 *
 * @param orgId Your Bluvo organization identifier.
 * @param projectId Your Bluvo project identifier.
 * @param customDomain Optional custom domain configuration to override default api-bluvo.com.
 *
 * @returns A new BluvoWebClient instance configured for local development.
 */
export function createDevWebClient({
	orgId,
	projectId,
	customDomain,
}: {
	orgId: string;
	projectId: string;
	customDomain?: string | "api-bluvo.com" | { api: string; ws: string };
}): BluvoWebClient {
	return BluvoWebClient.createClient({
		orgId,
		projectId,
		dev: true,
		customDomain,
	});
}
